import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
  CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import emailjs from '@emailjs/browser';

// Configuración de EmailJS
// Es necesario registrarse en EmailJS.com y seguir estos pasos:
// 1. Crear una cuenta en https://www.emailjs.com/
// 2. Añadir un servicio de correo conectando su cuenta de Gmail u otro servicio
// 3. Crear una plantilla de correo con las variables from_name, from_email, phone, company, subject, interested_in, message
// 4. Reemplazar los siguientes IDs con los proporcionados en su cuenta de EmailJS
const SERVICE_ID = 'service_g68w58a'; // Reemplazar con tu service ID de EmailJS
const TEMPLATE_ID = 'template_ho7zkf5'; // Reemplazar con tu template ID de EmailJS
const PUBLIC_KEY = 'tNRHJrz4p-L2_SK_-'; // Reemplazar con tu public key de EmailJS
const DESTINATION_EMAIL = 'contacto@mekasys.info';

const ContactPage = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    interestedIn: '',
    to_email: DESTINATION_EMAIL
  });
  const [errors, setErrors] = useState({});
  const [, setSubmitted] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!formData.interestedIn) {
      newErrors.interestedIn = 'Por favor selecciona una opción';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'El mensaje es requerido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setLoading(true);
      
      // Preparar los datos para enviarlos a EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'No proporcionado',
        company: formData.company || 'No proporcionada',
        subject: formData.subject || 'Consulta desde el sitio web',
        interested_in: getInterestText(formData.interestedIn),
        message: formData.message,
        to_email: DESTINATION_EMAIL
      };
      
      // Enviar el correo electrónico usando EmailJS
      emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY)
        .then((response) => {
          console.log('Correo enviado con éxito:', response);
          setSnackbarMessage('¡Mensaje enviado con éxito! Te contactaremos pronto.');
          setSnackbarSeverity('success');
          setOpenSnackbar(true);
          setSubmitted(true);
          
          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            subject: '',
            message: '',
            interestedIn: '',
            to_email: DESTINATION_EMAIL
          });
        })
        .catch((error) => {
          console.error('Error al enviar el correo:', error);
          setSnackbarMessage('Error al enviar el mensaje. Por favor, intenta nuevamente más tarde.');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const getInterestText = (value) => {
    switch(value) {
      case 'plan_basico':
        return 'Plan Básico';
      case 'plan_profesional':
        return 'Plan Profesional';
      case 'plan_personalizado':
        return 'Plan Personalizado';
      case 'soporte':
        return 'Soporte Técnico';
      case 'otro':
        return 'Otro';
      default:
        return 'No especificado';
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          pt: 12,
          pb: 6,
          background: 'linear-gradient(45deg, rgba(25,118,210,0.05) 0%, rgba(25,118,210,0.1) 100%)'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              variant="h2"
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              Contacto
            </Typography>
            <Typography
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: '800px', mx: 'auto', mb: 4, fontWeight: 400 }}
            >
              Estamos aquí para responder tus preguntas y ayudarte a comenzar con MekaSys
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Form and Info */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={7} lg={8}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    borderRadius: '12px',
                  }}
                >
                  <Typography variant="h4" fontWeight={600} gutterBottom>
                    Envíanos un mensaje
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Completa el formulario y te responderemos a la brevedad.
                  </Typography>

                  <Box component="form" ref={formRef} onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          label="Nombre"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          error={!!errors.name}
                          helperText={errors.name}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          error={!!errors.email}
                          helperText={errors.email}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Teléfono"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Empresa/Taller"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth required error={!!errors.interestedIn} disabled={loading}>
                          <InputLabel>Estoy interesado en</InputLabel>
                          <Select
                            name="interestedIn"
                            value={formData.interestedIn}
                            onChange={handleChange}
                            label="Estoy interesado en"
                          >
                            <MenuItem value="plan_basico">Plan Básico</MenuItem>
                            <MenuItem value="plan_profesional">Plan Profesional</MenuItem>
                            <MenuItem value="plan_personalizado">Plan Personalizado</MenuItem>
                            <MenuItem value="soporte">Soporte Técnico</MenuItem>
                            <MenuItem value="otro">Otro</MenuItem>
                          </Select>
                          {errors.interestedIn && (
                            <Typography variant="caption" color="error">
                              {errors.interestedIn}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Asunto"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          multiline
                          rows={4}
                          label="Mensaje"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          error={!!errors.message}
                          helperText={errors.message}
                          disabled={loading}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          sx={{ py: 1.5 }}
                          disabled={loading}
                        >
                          {loading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            'Enviar Mensaje'
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={5} lg={4}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card
                  elevation={3}
                  sx={{
                    borderRadius: '12px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'primary.main',
                    color: 'white',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Typography variant="h4" fontWeight={600} gutterBottom>
                      Información de Contacto
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.8, mb: 4 }}>
                      Otras formas de ponerte en contacto con nosotros:
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ mr: 2 }} />
                        <Typography variant="subtitle1" fontWeight={500}>
                          Email
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ ml: 5, opacity: 0.8 }}>
                        contacto@mekasys.info
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon sx={{ mr: 2 }} />
                        <Typography variant="subtitle1" fontWeight={500}>
                          Teléfono
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ ml: 5, opacity: 0.8 }}>
                        +56932647638
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccessTimeIcon sx={{ mr: 2 }} />
                        <Typography variant="subtitle1" fontWeight={500}>
                          Horario de Atención
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ ml: 5, opacity: 0.8 }}>
                        Lunes a Viernes: 9:00 - 18:00
                        <br />
                        Sábado: 10:00 - 14:00
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

                    <Typography variant="h6" fontWeight={500} gutterBottom>
                      Soporte Técnico
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Para clientes con planes activos:
                      <br />
                      soporte@mekasys.info
                      <br />
                      +56932647638
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage; 